// src/pages/admin/CategoriesAdmin.jsx
import { useEffect, useState, Fragment } from 'react'
import { doc, getDoc, setDoc } from 'firebase/firestore'
import { db } from '../../firebase'

// Petit helper pour générer un id propre
const slugify = (str = '') =>
  String(str)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

// Normalisation sous-catégorie
const normalizeChild = (sc = {}) => ({
  id: sc.id || sc.Id || '',
  label: String(sc.label || sc.Label || '').trim(),
  imageUrl: sc.imageUrl || sc.ImageUrl || '',
})

// Normalisation catégorie
const normalizeCat = (c = {}) => ({
  id: c.id || c.Id || '',
  label: String(c.label || c.Label || '').trim(),
  imageUrl: c.imageUrl || c.ImageUrl || '',
  children: Array.isArray(c.children)
    ? c.children.map(normalizeChild)
    : [],
})

export default function CategoriesAdmin() {
  const [cats, setCats] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [savedMessage, setSavedMessage] = useState('')
  const [expandedCatId, setExpandedCatId] = useState(null) // pour afficher les sous-cats

  // Formulaire nouvelle catégorie
  const [newCatLabel, setNewCatLabel] = useState('')
  const [newCatImageUrl, setNewCatImageUrl] = useState('')

  // Formulaires nouvelles sous-catégories (clé = cat.id)
  const [newSubLabelByCat, setNewSubLabelByCat] = useState({})
  const [newSubImageByCat, setNewSubImageByCat] = useState({})

  // Charger le doc categories/list
  useEffect(() => {
    ;(async () => {
      try {
        setLoading(true)
        setError(null)
        setSavedMessage('')

        const ref = doc(db, 'categories', 'list')
        const snap = await getDoc(ref)

        if (!snap.exists()) {
          setCats([])
          setError(
            "Le document 'categories/list' n'existe pas encore. " +
              'Crée-le dans Firestore avec un champ "data" (array).'
          )
          return
        }

        const raw = Array.isArray(snap.data()?.data)
          ? snap.data().data
          : []

        const normalized = raw
          .map(normalizeCat)
          .filter(c => c.id && c.label)

        setCats(normalized)
      } catch (e) {
        console.error(e)
        setError(e.message || 'Erreur lors du chargement des catégories.')
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  // Générer un id unique à partir d'un label
  const generateUniqueId = (label, existingIds = []) => {
    let base = slugify(label) || `cat-${Date.now()}`
    if (!existingIds.includes(base)) return base

    let i = 2
    let candidate = `${base}-${i}`
    while (existingIds.includes(candidate)) {
      i += 1
      candidate = `${base}-${i}`
    }
    return candidate
  }

  // Changement image catégorie
  const handleCatImageChange = (index, value) => {
    setCats(prev => {
      const copy = [...prev]
      copy[index] = { ...copy[index], imageUrl: value }
      return copy
    })
    setSavedMessage('')
  }

  // Changement image sous-catégorie
  const handleChildImageChange = (catIndex, childIndex, value) => {
    setCats(prev => {
      const copy = [...prev]
      const cat = copy[catIndex]
      const children = Array.isArray(cat.children) ? [...cat.children] : []
      children[childIndex] = {
        ...children[childIndex],
        imageUrl: value,
      }
      copy[catIndex] = { ...cat, children }
      return copy
    })
    setSavedMessage('')
  }

  // ➕ Création d'une nouvelle catégorie (en local)
  const handleAddCategory = e => {
    e.preventDefault()
    setError(null)
    setSavedMessage('')

    const label = newCatLabel.trim()
    if (!label) {
      setError('Le libellé de la catégorie est obligatoire.')
      return
    }

    const existingIds = cats.map(c => String(c.id || c.Id))
    const id = generateUniqueId(label, existingIds)

    const newCat = {
      id,
      label,
      imageUrl: newCatImageUrl.trim() || '',
      children: [],
    }

    setCats(prev => [...prev, newCat])
    setNewCatLabel('')
    setNewCatImageUrl('')
  }

  // ➕ Création d'une nouvelle sous-catégorie (en local)
  const handleAddSubCategory = catIndex => {
    setError(null)
    setSavedMessage('')

    const cat = cats[catIndex]
    const catId = String(cat.id || cat.Id)

    const label = (newSubLabelByCat[catId] || '').trim()
    const imageUrl = (newSubImageByCat[catId] || '').trim()

    if (!label) {
      setError('Le libellé de la sous-catégorie est obligatoire.')
      return
    }

    const children = Array.isArray(cat.children) ? cat.children : []
    const existingIds = children.map(ch => String(ch.id || ch.Id))
    const id = generateUniqueId(label, existingIds)

    const newChild = {
      id,
      label,
      imageUrl,
    }

    setCats(prev => {
      const copy = [...prev]
      const oldCat = copy[catIndex]
      const oldChildren = Array.isArray(oldCat.children)
        ? [...oldCat.children]
        : []
      copy[catIndex] = {
        ...oldCat,
        children: [...oldChildren, newChild],
      }
      return copy
    })

    setNewSubLabelByCat(prev => ({ ...prev, [catId]: '' }))
    setNewSubImageByCat(prev => ({ ...prev, [catId]: '' }))
  }

  // Sauvegarder TOUT (cats + children) dans categories/list.data
  const handleSaveAll = async () => {
    try {
      setSaving(true)
      setError(null)

      const payload = cats.map(c => ({
        id: c.id,
        label: c.label,
        imageUrl: c.imageUrl || '',
        children: Array.isArray(c.children)
          ? c.children.map(ch => ({
              id: ch.id,
              label: ch.label,
              imageUrl: ch.imageUrl || '',
            }))
          : [],
      }))

      const ref = doc(db, 'categories', 'list')
      await setDoc(ref, { data: payload }, { merge: true })

      setSavedMessage('Catégories et sous-catégories mises à jour ✅')
    } catch (e) {
      console.error(e)
      setError(e.message || 'Erreur lors de la sauvegarde.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0">Admin – Catégories & sous-catégories</h3>
        <button
          className="btn btn-primary btn-sm"
          onClick={handleSaveAll}
          disabled={saving || loading || !cats.length}
        >
          {saving ? 'Enregistrement…' : 'Enregistrer toutes les modifications'}
        </button>
      </div>

      {loading && (
        <div className="alert alert-info">Chargement des catégories…</div>
      )}

      {error && (
        <div className="alert alert-danger">
          {error}
        </div>
      )}

      {savedMessage && !saving && (
        <div className="alert alert-success">
          {savedMessage}
        </div>
      )}

      {/* Formulaire de création de catégorie */}
      <div className="card mb-3 border-0 shadow-sm">
        <div className="card-body">
          <h5 className="card-title mb-2">Ajouter une catégorie</h5>
          <p className="text-muted small mb-3">
            Les catégories sont les grandes familles visibles dans le menu (ex : Beauty, Électronique, Maison…)
          </p>
          <form
            className="row g-2 align-items-end"
            onSubmit={handleAddCategory}
          >
            <div className="col-md-4">
              <label className="form-label small">
                Libellé catégorie *
              </label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={newCatLabel}
                onChange={e => setNewCatLabel(e.target.value)}
                placeholder="Ex : Beauty"
                required
              />
            </div>
            <div className="col-md-5">
              <label className="form-label small">
                URL image (optionnel)
              </label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={newCatImageUrl}
                onChange={e => setNewCatImageUrl(e.target.value)}
                placeholder="https://…"
              />
            </div>
            <div className="col-md-3 d-flex">
              <button
                type="submit"
                className="btn btn-outline-primary btn-sm ms-auto"
                disabled={saving}
              >
                Ajouter la catégorie
              </button>
            </div>
          </form>
        </div>
      </div>

      {!loading && !error && cats.length === 0 && (
        <div className="alert alert-light border">
          Aucune catégorie trouvée dans <code>categories/list.data</code>.
        </div>
      )}

      {/* Tableau des catégories + sous-catégories */}
      {cats.length > 0 && (
        <div className="table-responsive">
          <table className="table align-middle">
            <thead>
              <tr>
                <th style={{ width: 60 }}>#</th>
                <th>ID</th>
                <th>Libellé</th>
                <th style={{ width: 160 }}>Aperçu</th>
                <th>URL de l’image catégorie</th>
                <th style={{ width: 160 }}>Sous-catégories</th>
              </tr>
            </thead>
            <tbody>
              {cats.map((c, idx) => {
                const isOpen = expandedCatId === c.id

                return (
                  <Fragment key={c.id || idx}>
                    <tr>
                      <td>{idx + 1}</td>
                      <td>
                        <code>{c.id}</code>
                      </td>
                      <td>{c.label}</td>
                      <td>
                        <div
                          style={{
                            width: 80,
                            height: 80,
                            borderRadius: 12,
                            overflow: 'hidden',
                            background: '#0b1226',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {c.imageUrl ? (
                            <img
                              src={c.imageUrl}
                              alt={c.label}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                              }}
                              onError={e => {
                                e.currentTarget.src =
                                  'https://via.placeholder.com/80x80?text=Img'
                              }}
                            />
                          ) : (
                            <span className="text-muted small text-center px-2">
                              Pas d’image
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="https://…"
                          value={c.imageUrl || ''}
                          onChange={e =>
                            handleCatImageChange(idx, e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() =>
                            setExpandedCatId(isOpen ? null : c.id)
                          }
                        >
                          {Array.isArray(c.children)
                            ? `${c.children.length} sous-cat.`
                            : '0 sous-cat.'}{' '}
                          {isOpen ? '▲' : '▼'}
                        </button>
                      </td>
                    </tr>

                    {/* Ligne détaillée pour les sous-catégories */}
                    {isOpen && (
                      <tr>
                        <td colSpan={6}>
                          {Array.isArray(c.children) &&
                          c.children.length > 0 ? (
                            <div className="table-responsive mb-3">
                              <table className="table table-sm align-middle mb-0">
                                <thead>
                                  <tr>
                                    <th style={{ width: 40 }}>#</th>
                                    <th>ID</th>
                                    <th>Libellé</th>
                                    <th style={{ width: 120 }}>Aperçu</th>
                                    <th>URL image sous-catégorie</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {c.children.map((ch, cIdx) => (
                                    <tr key={ch.id || cIdx}>
                                      <td>{cIdx + 1}</td>
                                      <td>
                                        <code>{ch.id}</code>
                                      </td>
                                      <td>{ch.label}</td>
                                      <td>
                                        <div
                                          style={{
                                            width: 60,
                                            height: 60,
                                            borderRadius: 10,
                                            overflow: 'hidden',
                                            background: '#0b1226',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                          }}
                                        >
                                          {ch.imageUrl ? (
                                            <img
                                              src={ch.imageUrl}
                                              alt={ch.label}
                                              style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                              }}
                                              onError={e => {
                                                e.currentTarget.src =
                                                  'https://via.placeholder.com/60x60?text=Img'
                                              }}
                                            />
                                          ) : (
                                            <span className="text-muted small text-center px-1">
                                              —
                                            </span>
                                          )}
                                        </div>
                                      </td>
                                      <td>
                                        <input
                                          type="text"
                                          className="form-control form-control-sm"
                                          placeholder="https://…"
                                          value={ch.imageUrl || ''}
                                          onChange={e =>
                                            handleChildImageChange(
                                              idx,
                                              cIdx,
                                              e.target.value
                                            )
                                          }
                                        />
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <div className="text-muted small mb-2">
                              Aucune sous-catégorie pour cette catégorie.
                            </div>
                          )}

                          {/* Formulaire ajout sous-catégorie */}
                          <form
                            className="row g-2 align-items-end small"
                            onSubmit={e => {
                              e.preventDefault()
                              handleAddSubCategory(idx)
                            }}
                          >
                            <div className="col-md-4">
                              <label className="form-label small mb-1">
                                Libellé sous-catégorie
                              </label>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                value={newSubLabelByCat[c.id] || ''}
                                onChange={e =>
                                  setNewSubLabelByCat(prev => ({
                                    ...prev,
                                    [c.id]: e.target.value,
                                  }))
                                }
                                placeholder="Ex : Skin Care"
                              />
                            </div>
                            <div className="col-md-5">
                              <label className="form-label small mb-1">
                                URL image (optionnel)
                              </label>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                value={newSubImageByCat[c.id] || ''}
                                onChange={e =>
                                  setNewSubImageByCat(prev => ({
                                    ...prev,
                                    [c.id]: e.target.value,
                                  }))
                                }
                                placeholder="https://…"
                              />
                            </div>
                            <div className="col-md-3 d-flex">
                              <button
                                type="submit"
                                className="btn btn-outline-primary btn-sm ms-auto"
                                disabled={saving}
                              >
                                + Ajouter la sous-catégorie
                              </button>
                            </div>
                          </form>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
